import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoComplete, Card, Empty, Spin, theme } from 'antd';
import * as actions from '@actions';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useDebouncedValue } from '@mantine/hooks';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import styled from '@emotion/styled';
import type { ReferralClinician } from 'store/actions/clinicians';
import { SessionFormItem } from '@components/Clinicians/Session/Components/SessionFormItem';
import { useSessionAction } from '@hooks/useSessionAction';
import { ACTION_REFF2F_CODE } from '@components/Clinicians/session-form';
import { useFaceToFaceFieldsetRequirements } from './FaceToFaceFieldset';

const ClinicianOptionRegion = styled(
  ({ town, region, ...rest }: { town?: string; region?: string } & React.HTMLAttributes<HTMLSpanElement>) => {
    if (!town && !region) return null;

    if (town && region) return <span {...rest}>{` (${town}, ${region})`}</span>;

    return <span {...rest}>{` (${town || region})`}</span>;
  }
)`
  opacity: 0.75;
`;

const SelectClinicianOptionDetailList = styled(
  ({ clinician, ...rest }: { clinician: Partial<ReferralClinician> } & React.HTMLAttributes<HTMLDivElement>) => (
    <div {...rest}>
      <div>
        <span>{clinician.hospital_name}</span>
        <ClinicianOptionRegion town={clinician.addline4 || undefined} region={clinician.region || undefined} />
      </div>
      <div>{clinician.cat_name}</div>
      {clinician.spec_areas && <div>{clinician.spec_areas}</div>}
    </div>
  )
)`
  display: flex;
  gap: 0.5em;
  font-size: 0.9em;

  > *:not(:last-child)::after {
    content: '•';
    opacity: 0.5;
    margin-left: 0.5em;
  }
`;

const SelectClinicianOption = styled(
  ({ clinician, ...rest }: { clinician: Partial<ReferralClinician> } & React.HTMLAttributes<HTMLDivElement>) => (
    <div {...rest}>
      {clinician.title && `${clinician.title} `}
      {clinician.first_name && `${clinician.first_name} `}
      {clinician.last_name && `${clinician.last_name}`}
      <SelectClinicianOptionDetailList clinician={clinician} />
    </div>
  )
)`
  display: flex;
  flex-direction: column;
`;

const SelectedClinicianPreview = () => {
  const { REFCLI_KEY } = useSessionAction(ACTION_REFF2F_CODE);
  const [referredClinician] = useSessionFormState(REFCLI_KEY);
  const { appointment } = useSessionAppointment();
  const { token } = theme.useToken();

  if (!referredClinician?.clin_user_guid) return null;

  const isDisabled = appointment.appointment_completed || appointment.read_only;

  return (
    <Card
      size="small"
      css={{
        marginBottom: '0.5em',
        ...(isDisabled && { background: token.colorBgContainerDisabled })
      }}
    >
      <div css={isDisabled ? { color: token.colorTextDisabled } : undefined}>
        <SelectClinicianOption clinician={referredClinician} />
      </div>
    </Card>
  );
};

export const SelectClinicianFormItem = ({ disabled = false }) => {
  const { t } = useTranslation('common');
  const { REFCLI_KEY, SPECIALITY_KEY, SUBSPECIALITY_KEY, POSTCODE_KEY } = useSessionAction(ACTION_REFF2F_CODE);

  const dispatch = useDispatch();
  const { appointment } = useSessionAppointment();

  const [speciality] = useSessionFormState(SPECIALITY_KEY);
  const [subSpeciality] = useSessionFormState(SUBSPECIALITY_KEY);

  const [referredClincician, setReferredClinician] = useSessionFormState(REFCLI_KEY);
  const [formStatePostcode, setFormStatePostcode] = useSessionFormState(POSTCODE_KEY, appointment.patient_postcode);

  const [searchInputValue, setSearchInputValue] = useState(formStatePostcode);
  const [debouncedSearch] = useDebouncedValue(searchInputValue || formStatePostcode || '', 500);

  const isPostcodeSearch = /\d/.test(debouncedSearch);
  const postcodeQuery = isPostcodeSearch ? debouncedSearch : '';
  const nameQuery = isPostcodeSearch ? '' : debouncedSearch;

  const { data: clinicians, status } = useQuery({
    queryKey: ['select_clinicians', speciality, postcodeQuery, subSpeciality, appointment.case_id, nameQuery],
    enabled: !!speciality && !!debouncedSearch,
    queryFn: () =>
      dispatch(
        actions.getRefClinicians(String(speciality), postcodeQuery, subSpeciality, appointment.case_id, nameQuery),
        false
      ),
    select: (data) => data.clinicians
  });

  const inputRequirements = useFaceToFaceFieldsetRequirements();

  const notFoundContent = useMemo(() => {
    if (status === 'pending')
      return (
        <Spin>
          <div css={{ height: 133 }} />
        </Spin>
      );
    if (status === 'error') return <div>{t('Error loading clinicians.')}</div>;

    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No clinicians found')} />;
  }, [status]);

  // Deselect the clinician if the speciality is changed
  useEffect(() => {
    if (speciality !== referredClincician?.cat_code) setReferredClinician(undefined);
  }, [speciality, referredClincician?.cat_code, setReferredClinician]);

  return (
    <SessionFormItem
      label={t('Select clinician')}
      extra="Enter a postcode or clinician name to find clinicians. Preset to patient’s home postcode."
      linkAria
    >
      <SelectedClinicianPreview />
      <AutoComplete
        data-testid="select-clinician"
        value={searchInputValue || ''}
        options={
          clinicians?.map((clinician) => ({
            value: `${clinician.clin_user_guid}/${clinician.hospital_id}`,
            label: <SelectClinicianOption clinician={clinician} />
          })) ?? []
        }
        onChange={(value?: string) => {
          if (value?.includes('/')) return;
          setSearchInputValue(value ?? '');
          if (value && /\d/.test(value)) setFormStatePostcode(value);
        }}
        onClear={() => setSearchInputValue('')}
        onSelect={(value?: string) => {
          if (!value) return;
          const clinician = clinicians?.find((c) => `${c.clin_user_guid}/${c.hospital_id}` === value);

          if (!clinician) return;

          setReferredClinician(clinician);
          setSearchInputValue('');
        }}
        notFoundContent={notFoundContent}
        disabled={
          appointment.appointment_completed ||
          appointment.read_only ||
          !speciality ||
          (inputRequirements[SUBSPECIALITY_KEY] === 'required' && !subSpeciality) ||
          disabled
        }
        filterOption={false}
        allowClear
        placeholder={t('Postcode or clinician name')}
      />
    </SessionFormItem>
  );
};
